import {Injectable, OnDestroy} from '@angular/core';
import {BehaviorSubject, Observable, of, throwError, Unsubscribable} from 'rxjs';
import {LoginStatus, FriendlyCaptchaStatus, CaptchaActionNames} from '../models';
import {UserService} from './user.service';
import {debounceTime, distinctUntilChanged, filter, map, mergeMap} from 'rxjs/operators';
import {fromPromise} from 'rxjs/internal-compatibility';
import {DistinguishSupportService} from "./distinguish-support.service";

/**
 * Service to assist with the FriendlyCaptcha to make sure the user is not a bot
 */
@Injectable({providedIn: 'root'})
export class FriendlyCaptchaService implements OnDestroy {
    private friendlyCaptchaStatus: BehaviorSubject<FriendlyCaptchaStatus> = new BehaviorSubject<FriendlyCaptchaStatus>(null);
    private friendlyCaptchaStatusVal: FriendlyCaptchaStatus;
    private token: BehaviorSubject<string> = new BehaviorSubject<string>(null);
    private _subUserStatus: Unsubscribable;
    private _subFriendlyCaptchaStatus: Unsubscribable;

    public getCurrentFriendlyCaptchaStatus(): Observable<FriendlyCaptchaStatus> {
        return this.friendlyCaptchaStatus.asObservable().pipe(filter(v => !!v));
    }

    /**
     *
     * @param {UserService} User
     * @param {DistinguishSupportService} distinguishSupportService
     */
    constructor(private User: UserService,
                private distinguishSupportService: DistinguishSupportService) {
        const $user = !sessionStorage.getItem('currentUser') ? this.User.currentLoggedInStatus() : of(null);
        $user.pipe(
            mergeMap((status) => this.setCurrentUserStatus(status)),
            mergeMap(userStatus => {
                this.setFriendlyCaptchaStatus(userStatus);
                return this.getCurrentFriendlyCaptchaStatus();
            })).subscribe();
    }

    /**
     * @ignore
     */
    ngOnDestroy(): void {
        this._subUserStatus.unsubscribe();
        this._subFriendlyCaptchaStatus.unsubscribe();
    }

    /**
     * Check to see if the FriendlyCaptcha is enabled
     *
     * @param obs
     */
    public isFriendlyCaptchaEnabled(obs: true): Observable<boolean>;
    public isFriendlyCaptchaEnabled(obs: false): boolean;
    public isFriendlyCaptchaEnabled(obs: boolean): Observable<boolean> | boolean {
        if (obs) {
            return this.getCurrentFriendlyCaptchaStatus().pipe(
                map(v => v.enabled)
            );
        }
        return this.friendlyCaptchaStatusVal ? this.friendlyCaptchaStatusVal.enabled : false;
    }

    /**
     * Get the Site Key
     *
     * @param obs
     */
    public getSiteKey(obs: true): Observable<string>;
    public getSiteKey(obs: false): string;
    public getSiteKey(obs: boolean): Observable<string> | string {
        if (obs) {
            return this.getCurrentFriendlyCaptchaStatus().pipe(
                map(v => v.siteKey)
            );
        }
        return this.friendlyCaptchaStatusVal ? this.friendlyCaptchaStatusVal.siteKey : null;
    }

    /**
     * Get the FriendlyCaptcha token
     *
     */
    public getToken(): Observable<string> {
        return this.token.asObservable();
    }

    public setToken(token: string) {
        this.token.next(token);
    }

    public checkCaptchaResponse(response: string, friendlyCaptchaStatus: FriendlyCaptchaStatus, actionName: CaptchaActionNames):Observable<any> {
        if (!response && friendlyCaptchaStatus?.enabled && friendlyCaptchaStatus?.actionNames.includes(actionName) && !this.distinguishSupportService.support) {
            return throwError({
                error: {
                    message: 'ERROR_CAPTCHA_UNCHECKED',
                    errors: [{code: 'ERROR_CAPTCHA_UNCHECKED'}]
                }
            });
        }
        return of(response);
    }

    private setCurrentUserStatus(status): Observable<LoginStatus> {
        if (status) {
            sessionStorage.setItem('currentUser', JSON.stringify(status));
        }
        return this.User.getCurrentUserStatus();
    }

    private setFriendlyCaptchaStatus(userStatus) {
        const friendlyCaptcha = userStatus?.friendlyCaptcha;
        if (!this.friendlyCaptchaStatusVal && friendlyCaptcha
            || this.friendlyCaptchaStatusVal && friendlyCaptcha && (this.friendlyCaptchaStatusVal.enabled !== friendlyCaptcha.enabled
                || friendlyCaptcha.enabled && this.friendlyCaptchaStatusVal.siteKey !== friendlyCaptcha.siteKey)) {
            this.friendlyCaptchaStatusVal = friendlyCaptcha;
            this.friendlyCaptchaStatus.next(this.friendlyCaptchaStatusVal);
        }
    }
}
